import {AUTH_STORAGE_KEY} from '@ark/web';

const HANDOFF_PARAM = 'handoff';
const REQUEST_TIMEOUT_MS = 4000;

const apiRoot = import.meta.env.VITE_API_ROOT || '';

const legacyHosts = (import.meta.env.VITE_LEGACY_ENT_HOSTS || '')
  .split(',')
  .map((host: string) => host.trim().toLowerCase())
  .filter(Boolean);

const handoffTarget = (): URL | null => {
  try {
    return new URL(import.meta.env.VITE_HANDOFF_TARGET || '');
  } catch {
    return null;
  }
};

const readToken = () => {
  try {
    return localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
};

const postJson = async (path: string, body: object, token?: string) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiRoot}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && {Authorization: `Token ${token}`}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

const moveToTarget = async (target: URL) => {
  const destination = new URL(target.origin);
  destination.pathname = window.location.pathname.replace(/^\/+/, '/');
  destination.search = window.location.search;
  if (destination.origin !== target.origin) {
    window.location.replace(target.origin);
    return;
  }
  const token = readToken();
  if (token) {
    const minted = await postJson('/auth/handoff/', {}, token);
    if (typeof minted?.code === 'string') {
      destination.hash = `${HANDOFF_PARAM}=${encodeURIComponent(minted.code)}`;
    }
  }
  window.location.replace(destination.toString());
};

const readHandoffCode = () => {
  const params = new URLSearchParams(window.location.hash.slice(1));
  return params.get(HANDOFF_PARAM);
};

const clearHandoffFromUrl = () => {
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}`,
  );
};

const cameFromLegacyHost = () => {
  try {
    return legacyHosts.includes(
      new URL(document.referrer).hostname.toLowerCase(),
    );
  } catch {
    return false;
  }
};

const redeemHandoff = async (code: string) => {
  const redeemed = await postJson('/auth/handoff/redeem/', {code});
  if (typeof redeemed?.auth_token !== 'string') return false;
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, redeemed.auth_token);
    return true;
  } catch {
    return false;
  }
};

type HandoffOutcome = 'redirecting' | 'redeemed' | 'continue';

export const runLoginHandoff = async (): Promise<HandoffOutcome> => {
  const target = handoffTarget();
  const host = window.location.hostname.toLowerCase();
  if (target && legacyHosts.includes(host) && host !== target.hostname) {
    await moveToTarget(target);
    return 'redirecting';
  }
  const code = readHandoffCode();
  if (!code) return 'continue';
  clearHandoffFromUrl();
  const isTarget = target?.origin === window.location.origin;
  if (!isTarget || !cameFromLegacyHost() || readToken()) return 'continue';
  return (await redeemHandoff(code)) ? 'redeemed' : 'continue';
};
